import AnatomyIcon from "@images/svgIcons/profile/anatomyIcon"
import BookIcon from "@images/svgIcons/profile/bookIcon"
import CrossFitIcon from "@images/svgIcons/profile/crossfitIcon"
import HomeIcon from "@images/svgIcons/profile/homeIcon"
import IdentityIcon from "@images/svgIcons/profile/identityIcon"
import { UserProfile } from "@modules/auth/userProfileTypes"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { AnatomyForm } from "@modules/profile/myProfile/components/profileAnatomyForm"
import { BiographyForm } from "@modules/profile/myProfile/components/profileBiographyForm"
import { ContactForm } from "@modules/profile/myProfile/components/profileContactForm"
import { CrossfitInformationForm } from "@modules/profile/myProfile/components/profileCrossfitInformationForm"
import { ProfileIdentifyForm } from "@modules/profile/myProfile/components/profileIdentifyForm"
import { useProfileFormik } from "@modules/profile/myProfile/hooks/useProfileFormik"
import { getValidationSchema, ProfileFormTabProps } from "@modules/profile/myProfile/profileFormikType"
import { HUButton } from "@modules/ui/components/huButton"
import { useToast } from "@modules/ui/components/huToast"
import { MenuItem } from "primereact/menuitem"
import { TabMenu, TabMenuTabChangeEvent } from "primereact/tabmenu"
import { useCallback, useMemo } from "react"
import styled, { useTheme } from "styled-components"

type Props = {
  user: UserProfile
}

export const ProfileForm: React.FC<Props> = ({ user }) => {
  const theme = useTheme()
  const toast = useToast()

  const { formik, isSubmitting, activeIndex, setActiveIndex } = useProfileFormik(user)

  const tabContent = useMemo(() => {
    const props: ProfileFormTabProps = {
      isFormFieldInValid: (input) => isFormFieldInValid(input, formik),
      getFormErrorMessage: (input, errorMessage) => getFormErrorMessage(input, formik, { errorMessage }),
      formik: {
        values: formik.values,
        initialValues: formik.initialValues,
        getFieldProps: formik.getFieldProps,
        setFieldValue: formik.setFieldValue,
      },
    }
    switch (activeIndex) {
      case 0:
        return <ProfileIdentifyForm {...props} />
      case 1:
        return <CrossfitInformationForm {...props} />
      case 2:
        return <BiographyForm {...props} />
      case 3:
        return <ContactForm {...props} />
      case 4:
        return <AnatomyForm {...props} />
      default:
        return <ProfileIdentifyForm {...props} />
    }
  }, [activeIndex, JSON.stringify(formik.values), JSON.stringify(formik.errors), JSON.stringify(formik.touched)])

  const iconColor = useCallback(
    (iconIndex: number) => (iconIndex === activeIndex ? theme.neutralColor700 : theme.neutralColor),
    [activeIndex],
  )

  const items: MenuItem[] = [
    {
      label: word("profile.form.identity.tabLabel"),
      icon: <IdentityIcon color={iconColor(0)} className="mr-1" />,
    },
    {
      label: word("profile.form.crossfit.tabLabel"),
      icon: <CrossFitIcon color={iconColor(1)} className="mr-1" />,
    },
    {
      label: word("profile.form.biography.tabLabel"),
      icon: <BookIcon color={iconColor(2)} className="mr-1" />,
    },
    {
      label: word("profile.form.contact.tabLabel"),
      icon: <HomeIcon color={iconColor(3)} className="mr-1" />,
    },
    {
      label: word("profile.form.anatomy.tabLabel"),
      icon: <AnatomyIcon color={iconColor(4)} className="mr-1" />,
    },
  ]

  const onTabChange = async (e: TabMenuTabChangeEvent) => {
    getValidationSchema(activeIndex)
      .validate(formik.values, { abortEarly: false })
      .then(() => setActiveIndex(e.index))
      .catch((err) => {
        toast.show({
          severity: "warn",
          summary: err.inner[0].message,
        })
      })
  }

  return (
    <form className="flex flex-column flex-grow-1 justify-content-between">
      {/* NAVIGATION TABS */}
      <div className="flex w-full">
        <StyledTabMenu model={items} activeIndex={activeIndex} onTabChange={onTabChange} />
      </div>

      {/* Form content */}
      {tabContent}

      {/* Submit button */}
      <HUButton
        loading={isSubmitting}
        disabled={isSubmitting}
        text={word("global.save")}
        onClick={() => formik.submitForm()}
        colorType="Primary"
        type="Default"
        size="M"
        className="align-self-end mt-2"
      />
    </form>
  )
}

const StyledTabMenu = styled(TabMenu)`
  border: none;
  .p-tabmenu-nav {
    flex-wrap: wrap;
    .p-tabmenuitem .p-menuitem-link {
      height: 100%;
    }
    .p-tabmenuitem.p-highlight .p-menuitem-link {
      border-width: 0 0 2px 0;
    }
  }
`
